import { getClient } from "../graphQL/query/client";
import { authorization } from "../module/auth";
import { ClientType } from "../store/types";

/**
 * Extended client type that includes payment skip settings
 */
export interface ClientWithPaymentSettings {
  id: string;
  logo: string;
  name: string;
  countryId: string;
  createdBy: string;
  isSkipCheckoutEnabled?: boolean; // Client-level payment skip setting
}

/**
 * Campaign type with payment skip setting
 */
export interface CampaignWithPaymentSkip {
  isPaymentSkipped?: boolean;
  [key: string]: any;
}

/**
 * Fetch client data with payment skip settings
 * @param clientId - The client ID to fetch data for
 * @returns Promise<ClientWithPaymentSettings | null>
 */
export const fetchClientPaymentSettings = async (
  clientId?: string | number
): Promise<ClientWithPaymentSettings | null> => {
  try {
    if (!clientId) {
      console.warn("PaymentSkip: No clientId provided for fetching client payment settings");
      return null;
    }

    const clientData = await getClient({ clientId: String(clientId) });

    if (!clientData || !clientData[0]) {
      console.warn(`PaymentSkip: No client data found for clientId: ${clientId}`);
      return null;
    }

    return clientData[0] as ClientWithPaymentSettings;
  } catch (error) {
    console.error("PaymentSkip: Error fetching client payment settings:", error);
    return null;
  }
};

/**
 * Determine if payment should be skipped using hierarchical logic
 * Campaign-level setting takes precedence over client-level setting
 *
 * @param campaign - Campaign object with potential isPaymentSkipped property
 * @param clientId - Optional client ID for fetching client-level settings
 * @returns Promise<boolean> - True if payment should be skipped
 */
export const shouldSkipPayment = async (
  campaign: CampaignWithPaymentSkip,
  clientId?: string | number
): Promise<boolean> => {
  try {
    // Priority 1: Campaign-level payment skip setting
    if (campaign.isPaymentSkipped !== undefined) {
      console.log(`PaymentSkip: Using campaign-level setting: ${campaign.isPaymentSkipped}`);
      return campaign.isPaymentSkipped;
    }

    // Priority 2: Client-level payment skip setting
    const effectiveClientId = clientId || authorization.getUser()?.clientId;

    if (effectiveClientId) {
      const clientSettings = await fetchClientPaymentSettings(effectiveClientId);

      if (clientSettings?.isSkipCheckoutEnabled !== undefined) {
        console.log(`PaymentSkip: Using client-level setting: ${clientSettings.isSkipCheckoutEnabled}`);
        return clientSettings.isSkipCheckoutEnabled;
      }
    }

    // Default: Payment is required
    console.log("PaymentSkip: No payment skip settings found, defaulting to payment required");
    return false;
  } catch (error) {
    console.error("PaymentSkip: Error in shouldSkipPayment logic:", error);
    // Default to false (payment required) on error
    return false;
  }
};

/**
 * Synchronous version that only checks campaign-level setting
 * Use this when client data is not available or when you only need campaign-level check
 *
 * @param campaign - Campaign object with potential isPaymentSkipped property
 * @returns boolean - True if payment should be skipped based on campaign setting only
 */
export const shouldSkipPaymentSync = (campaign: CampaignWithPaymentSkip): boolean => {
  return campaign.isPaymentSkipped ?? false;
};

/**
 * Hook-like function for React components that need payment skip logic
 * This can be used in components where you have both campaign and client data available
 *
 * @param campaign - Campaign object (optional, can be null/undefined)
 * @param client - Client object (optional, can be null/undefined)
 * @returns boolean - True if payment should be skipped
 */
export const getPaymentSkipStatus = (
  campaign?: CampaignWithPaymentSkip | null,
  client?: ClientWithPaymentSettings | ClientType | null
): boolean => {
  return (campaign?.isPaymentSkipped ?? false) || (client?.isSkipCheckoutEnabled ?? false);
};
